import { useState } from 'react';
import httpClient from '../../services/httpClient';
import { maskPhone, maskCPF, maskCNPJ, maskZipCode, maskDate } from '../../utils/masks';
import { EyeIcon, EyeOffIcon } from '../Layout/icons';
import SuccessScreen from './SuccessScreen';
import MessageModal from '../shared/MessageModal';
import {
    Container,
    SignUpForm,
    Input,
    Button,
    Title,
    Footer,
    InputGroup,
    FormGroup,
    Label,
    Select,
    SectionTitle,
    RemoveButton,
    PasswordInputWrapper,
    TogglePasswordButton
} from './styles';

export default function SignUp({ onSwitchToLogin }) {
    const [isSuccess, setIsSuccess] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const [formData, setFormData] = useState({
        responsibleName: '',
        responsibleEmail: '',
        responsibleDocument: '',
        responsibleBirthdate: '',
        responsiblePhoneNumbers: [''],
        zipCode: '',
        street: '',
        number: '',
        complement: '',
        district: '',
        city: '',
        stateAbbr: '',
        stateName: '',
        document: '',
        name: '',
        fantasyName: '',
        registrationStatus: 'ACTIVE',
        password: '',
        confirmPassword: '',
    });

    const [addressLocked, setAddressLocked] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    if (isSuccess) {
        return <SuccessScreen onGoToLogin={onSwitchToLogin} />;
    }

    function handleChange(event) {
        const { name, value } = event.target;

        let maskedValue = value;

        if (name === 'responsibleDocument') maskedValue = maskCPF(value);
        if (name === 'document') maskedValue = maskCNPJ(value);
        if (name === 'zipCode') {
            maskedValue = maskZipCode(value);
            if (maskedValue.length === 9) {
                fetchAddress(maskedValue);
            }
        }
        if (name === 'responsibleBirthdate') maskedValue = maskDate(value);

        setFormData((prev) => ({
            ...prev,
            [name]: maskedValue,
        }));
    }

    function handlePhoneChange(index, value) {
        const newPhones = [...formData.responsiblePhoneNumbers];
        newPhones[index] = maskPhone(value);
        setFormData((prev) => ({ ...prev, responsiblePhoneNumbers: newPhones }));
    }

    function handleAddPhone() {
        setFormData((prev) => ({
            ...prev,
            responsiblePhoneNumbers: [...prev.responsiblePhoneNumbers, ''],
        }));
    }

    function handleRemovePhone(index) {
        const newPhones = formData.responsiblePhoneNumbers.filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, responsiblePhoneNumbers: newPhones }));
    }

    async function fetchAddress(zipCode) {
        const cleanZip = zipCode.replace(/\D/g, '');
        if (cleanZip.length !== 8) return;

        try {
            const response = await httpClient.get(`/api/enderecos/${cleanZip}`);
            if (response.ok) {
                const data = await response.json();
                setFormData((prev) => ({
                    ...prev,
                    street: data.street,
                    district: data.district,
                    city: data.city,
                    stateAbbr: data.state.abbreviation,
                    stateName: data.state.name,
                }));
                setAddressLocked(true);
            } else {
                throw new Error('Address not found');
            }
        } catch (error) {
            setAddressLocked(false);
            // Clear fields to allow manual entry if they were previously filled
            setFormData((prev) => ({
                ...prev,
                street: '',
                district: '',
                city: '',
                stateAbbr: '',
                stateName: '',
            }));
        }
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'As senhas não coincidem!',
            });
            return;
        }

        setIsLoading(true);

        const payload = {
            legalPersonData: {
                responsibleName: formData.responsibleName,
                responsibleEmail: formData.responsibleEmail,
                responsiblePhoneNumbers: formData.responsiblePhoneNumbers
                    .filter(p => p.length > 0)
                    .map(p => ({ number: p })),
                responsibleDocument: formData.responsibleDocument,
                responsibleBirthdate: formData.responsibleBirthdate.split('/').reverse().join('-'), // Convert DD/MM/YYYY to YYYY-MM-DD
                responsibleAddress: {
                    street: formData.street,
                    number: formData.number,
                    district: formData.district,
                    city: formData.city,
                    zipCode: formData.zipCode,
                    complement: formData.complement,
                    state: {
                        abbreviation: formData.stateAbbr,
                        name: formData.stateName || formData.stateAbbr, // Fallback if manual entry didn't provide name
                    },
                },
                document: formData.document,
                name: formData.name,
                fantasyName: formData.fantasyName,
                registrationStatus: formData.registrationStatus,
            },
            password: formData.password,
        };

        try {
            const response = await httpClient.post('/usuarios', payload);

            if (response.ok) {
                setIsSuccess(true);
            } else {
                setMessageModal({
                    open: true,
                    title: 'Erro',
                    message: 'Falha no cadastro. Verifique os dados e tente novamente.',
                });
            }
        } catch (error) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: 'Ocorreu um erro ao tentar cadastrar.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container>
            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
            <Title>Cadastro de Empresa</Title>
            <SignUpForm onSubmit={handleSubmit}>

                <SectionTitle>Dados do Responsável</SectionTitle>

                <FormGroup>
                    <Label>Nome Completo</Label>
                    <Input
                        name="responsibleName"
                        placeholder="Ex: Felipe Carvalho"
                        value={formData.responsibleName}
                        onChange={handleChange}
                        required
                    />
                </FormGroup>

                <InputGroup>
                    <FormGroup flex={2}>
                        <Label>E-mail</Label>
                        <Input
                            name="responsibleEmail"
                            type="email"
                            placeholder="Ex: email@empresa.com"
                            value={formData.responsibleEmail}
                            onChange={handleChange}
                            required
                        />
                    </FormGroup>
                    <FormGroup flex={1}>
                        <Label>CPF</Label>
                        <Input
                            name="responsibleDocument"
                            placeholder="000.000.000-00"
                            value={formData.responsibleDocument}
                            onChange={handleChange}
                            maxLength={14}
                            required
                        />
                    </FormGroup>
                </InputGroup>

                <FormGroup>
                    <Label>Data de Nascimento</Label>
                    <Input
                        name="responsibleBirthdate"
                        placeholder="DD/MM/AAAA"
                        value={formData.responsibleBirthdate}
                        onChange={handleChange}
                        maxLength={10}
                        required
                    />
                </FormGroup>

                <Label>Telefones</Label>
                {formData.responsiblePhoneNumbers.map((phone, index) => (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column', marginBottom: 8 }}>
                        <Input
                            placeholder="(XX) XXXXX-XXXX"
                            value={phone}
                            onChange={(e) => handlePhoneChange(index, e.target.value)}
                            required
                        />
                        {index > 0 && (
                            <RemoveButton type="button" onClick={() => handleRemovePhone(index)}>
                                Remover telefone
                            </RemoveButton>
                        )}
                    </div>
                ))}
                <Button type="button" variant="secondary" onClick={handleAddPhone} style={{ marginTop: 0, marginBottom: 16 }}>
                    Adicionar outro telefone
                </Button>

                <SectionTitle>Endereço do Responsável</SectionTitle>

                <InputGroup>
                    <FormGroup flex={0.6}>
                        <Label>CEP</Label>
                        <Input
                            name="zipCode"
                            placeholder="00000-000"
                            value={formData.zipCode}
                            onChange={handleChange}
                            maxLength={9}
                            required
                        />
                    </FormGroup>
                    <FormGroup flex={1.4}>
                        <Label>Rua</Label>
                        <Input
                            name="street"
                            value={formData.street}
                            onChange={handleChange}
                            disabled={addressLocked}
                            required
                        />
                    </FormGroup>
                </InputGroup>

                <InputGroup>
                    <FormGroup flex={0.5}>
                        <Label>Número</Label>
                        <Input
                            name="number"
                            value={formData.number}
                            onChange={handleChange}
                            required
                        />
                    </FormGroup>
                    <FormGroup flex={1.5}>
                        <Label>Complemento</Label>
                        <Input
                            name="complement"
                            value={formData.complement}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </InputGroup>

                <InputGroup>
                    <FormGroup flex={1.5}>
                        <Label>Bairro</Label>
                        <Input
                            name="district"
                            value={formData.district}
                            onChange={handleChange}
                            disabled={addressLocked}
                            required
                        />
                    </FormGroup>
                    <FormGroup flex={1}>
                        <Label>Cidade</Label>
                        <Input
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            disabled={addressLocked}
                            required
                        />
                    </FormGroup>
                    <FormGroup flex={0.4}>
                        <Label>UF</Label>
                        <Input
                            name="stateAbbr"
                            value={formData.stateAbbr}
                            onChange={handleChange}
                            disabled={addressLocked}
                            maxLength={2}
                            required
                        />
                    </FormGroup>
                </InputGroup>

                <SectionTitle>Dados da Empresa</SectionTitle>

                <InputGroup>
                    <FormGroup>
                        <Label>CNPJ</Label>
                        <Input
                            name="document"
                            placeholder="00.000.000/0000-00"
                            value={formData.document}
                            onChange={handleChange}
                            maxLength={18}
                            required
                            style={{ textTransform: 'uppercase' }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Situação Cadastral</Label>
                        <Select
                            name="registrationStatus"
                            value={formData.registrationStatus}
                            onChange={handleChange}
                        >
                            <option value="ACTIVE">Ativa</option>
                            <option value="SUSPENDED">Suspensa</option>
                            <option value="UNFIT">Inapta</option>
                            <option value="CLOSED">Baixada</option>
                            <option value="NULL">Nula</option>
                        </Select>
                    </FormGroup>
                </InputGroup>

                <FormGroup>
                    <Label>Razão Social</Label>
                    <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Nome Fantasia</Label>
                    <Input
                        name="fantasyName"
                        value={formData.fantasyName}
                        onChange={handleChange}
                        required
                    />
                </FormGroup>

                <SectionTitle>Segurança</SectionTitle>

                <InputGroup>
                    <FormGroup>
                        <Label>Senha</Label>
                        <PasswordInputWrapper>
                            <Input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                            <TogglePasswordButton
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                            >
                                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </TogglePasswordButton>
                        </PasswordInputWrapper>
                    </FormGroup>
                    <FormGroup>
                        <Label>Confirmar Senha</Label>
                        <PasswordInputWrapper>
                            <Input
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                            />
                            <TogglePasswordButton
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                            >
                                {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </TogglePasswordButton>
                        </PasswordInputWrapper>
                    </FormGroup>
                </InputGroup>

                <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Cadastrando...' : 'Finalizar Cadastro'}
                </Button>
            </SignUpForm>
            <Footer>
                Já tem uma conta?
                <button type="button" onClick={onSwitchToLogin}>
                    Fazer Login
                </button>
            </Footer>
        </Container>
    );
}
